import type { FieldErrorCode as GeneratedFieldErrorCode } from '~/services/api/generated'
import { PhoneMessageKey } from '~/utils/PhoneMessageKey'
import { formatCurrency } from '~/utils/formatCurrency'
import { formatFieldIssueLimit } from '~/utils/formatFieldIssueLimit'
import { formatFileSize } from '~/utils/formatFileSize'
import { expectedFormatKey } from './expectedFormatKey'
import { expectedTypeKey } from './expectedTypeKey'
import type { FieldErrorDisplayKey } from './FieldErrorDisplayKey'
import type { FieldErrorPresenter } from './FieldErrorPresenter'
import { isExpectedFormat } from './isExpectedFormat'
import { isExpectedType } from './isExpectedType'
import { plainFieldErrorPresenter } from './plainFieldErrorPresenter'
import { presentFieldIssueParams } from './presentFieldIssueParams'
import { readFieldIssueParam } from './readFieldIssueParam'
import { useLogger } from './useLogger'

const logger = useLogger('FieldErrorPresenter')

const invalidFormat: FieldErrorPresenter = (params, t) => {
  const expected = readFieldIssueParam.text(params, 'expected_format') ?? ''
  if (expected === 'phone') return { key: PhoneMessageKey.invalid, values: {} }
  const isKnown = isExpectedFormat(expected)
  if (!isKnown) logger.error('unknownExpectedFormat', { expected })
  const nounKey = isKnown ? expectedFormatKey[expected] : 'presentation.errors.expectedFormat.value'

  return {
    key: 'presentation.errors.field.invalidFormat',
    values: { expected_format: t(nounKey) }
  }
}

const invalidType: FieldErrorPresenter = (params, t) => {
  const expected = readFieldIssueParam.text(params, 'expected_type') ?? ''
  const isKnown = isExpectedType(expected)
  if (!isKnown) logger.error('unknownExpectedType', { expected })

  return {
    key: 'presentation.errors.field.invalidType',
    values: {
      expected_type: t(
        isKnown ? expectedTypeKey[expected] : 'presentation.errors.expectedType.value'
      )
    }
  }
}

const outOfRange: FieldErrorPresenter = (params, t) => {
  const min = readFieldIssueParam.number(params, 'min')
  const max = readFieldIssueParam.number(params, 'max')
  const values = presentFieldIssueParams(params, t)
  if (min !== undefined && max !== undefined) {
    return { key: 'presentation.errors.field.outOfRange', values }
  }
  return min === undefined
    ? { key: 'presentation.errors.field.atMost', values }
    : { key: 'presentation.errors.field.atLeast', values }
}

const tooSmall: FieldErrorPresenter = (params) => {
  const min = readFieldIssueParam.number(params, 'min') ?? 0
  const unit = readFieldIssueParam.text(params, 'unit')
  const isExclusive = readFieldIssueParam.flag(params, 'exclusive')
  const shown = unit === 'currency' ? formatCurrency(min) : min
  const values = { min: unit === 'kilobytes' ? formatFileSize(min) : shown }

  return isExclusive
    ? { key: 'presentation.errors.field.tooSmallExclusive', values }
    : { key: 'presentation.errors.field.atLeast', values }
}

const REQUIRED_BY_GROUP: ReadonlyMap<string, FieldErrorDisplayKey> = new Map([
  ['categories', 'presentation.errors.field.requiredCategories'],
  ['custom_hours', 'presentation.errors.field.requiredCustomHours'],
  ['items', 'presentation.errors.field.requiredItems'],
  ['stores', 'presentation.errors.field.requiredStores']
])

const TOO_FEW_BY_GROUP: ReadonlyMap<string, FieldErrorDisplayKey> = new Map([
  ['categories', 'presentation.errors.field.tooFewCategories'],
  ['custom_hours', 'presentation.errors.field.tooFewCustomHours'],
  ['dining_style', 'presentation.errors.field.tooFewDiningStyle'],
  ['ideal_for', 'presentation.errors.field.tooFewIdealFor'],
  ['items', 'presentation.errors.field.tooFewItems'],
  ['published_items', 'presentation.errors.field.tooFewPublishedItems'],
  ['stores', 'presentation.errors.field.tooFewStores']
])

const required: FieldErrorPresenter = (params) => {
  const group = readFieldIssueParam.text(params, 'group') ?? ''

  return {
    key: REQUIRED_BY_GROUP.get(group) ?? 'presentation.errors.field.required',
    values: {}
  }
}

const tooFew: FieldErrorPresenter = (params) => {
  const group = readFieldIssueParam.text(params, 'group') ?? ''
  const isOptionGroup = readFieldIssueParam.number(params, 'group_id') !== undefined
  const fallbackKey = isOptionGroup
    ? 'presentation.errors.field.tooFewInOptionGroup'
    : 'presentation.errors.field.tooFew'

  return {
    key: TOO_FEW_BY_GROUP.get(group) ?? fallbackKey,
    values: { min: readFieldIssueParam.number(params, 'min') ?? 0 }
  }
}

const tooMany: FieldErrorPresenter = (params) => {
  const isOption = readFieldIssueParam.number(params, 'option_id') !== undefined
  const isOptionGroup = readFieldIssueParam.number(params, 'group_id') !== undefined
  const values = { max: readFieldIssueParam.number(params, 'max') ?? 0 }
  if (isOption) return { key: 'presentation.errors.field.tooManyOfOption', values }
  if (isOptionGroup) return { key: 'presentation.errors.field.tooManyInOptionGroup', values }

  return { key: 'presentation.errors.field.tooMany', values }
}

const notFound: FieldErrorPresenter = (params) => {
  const isOptionGroup = readFieldIssueParam.number(params, 'group_id') !== undefined

  return {
    key: isOptionGroup
      ? 'presentation.errors.field.notFoundOptionGroup'
      : 'presentation.errors.field.notFound',
    values: {}
  }
}

const fileTooLarge: FieldErrorPresenter = (params) => ({
  key: 'presentation.errors.field.fileTooLarge',
  values: { max: formatFileSize(readFieldIssueParam.number(params, 'max_kilobytes') ?? 0) }
})

const dateBound =
  (earlier: boolean): FieldErrorPresenter =>
  (params, t) => {
    const otherField = readFieldIssueParam.text(params, 'other_field')
    const limit = readFieldIssueParam.text(params, 'limit') ?? ''
    const isInclusive = readFieldIssueParam.flag(params, 'inclusive')
    const hasField = otherField !== undefined
    const values = hasField
      ? presentFieldIssueParams({ other_field: otherField }, t)
      : { limit: formatFieldIssueLimit(limit) }
    const keys = earlier
      ? ([
          [
            'presentation.errors.field.dateTooEarly',
            'presentation.errors.field.dateTooEarlyInclusive'
          ],
          [
            'presentation.errors.field.dateTooEarlyField',
            'presentation.errors.field.dateTooEarlyFieldInclusive'
          ]
        ] as const)
      : ([
          [
            'presentation.errors.field.dateTooLate',
            'presentation.errors.field.dateTooLateInclusive'
          ],
          [
            'presentation.errors.field.dateTooLateField',
            'presentation.errors.field.dateTooLateFieldInclusive'
          ]
        ] as const)
    const [exclusiveKey, inclusiveKey] = keys[hasField ? 1 : 0]

    return { key: isInclusive ? inclusiveKey : exclusiveKey, values }
  }

/** Presentation for every code the API emits; a code added to the contract fails this build until it is given one. */
export const fieldErrorPresenterByCode = Object.freeze({
  FIELD_REQUIRED: required,
  FIELD_INVALID_FORMAT: invalidFormat,
  FIELD_INVALID_TYPE: invalidType,
  FIELD_TOO_SHORT: plainFieldErrorPresenter('presentation.errors.field.tooShort'),
  FIELD_TOO_LONG: plainFieldErrorPresenter('presentation.errors.field.tooLong'),
  FIELD_OUT_OF_RANGE: outOfRange,
  FIELD_ALREADY_EXISTS: plainFieldErrorPresenter('presentation.errors.field.alreadyExists'),
  FIELD_NOT_FOUND: notFound,
  FIELD_MUST_MATCH: plainFieldErrorPresenter('presentation.errors.field.mustMatch'),
  FIELD_INVALID_ENUM_VALUE: plainFieldErrorPresenter('presentation.errors.field.invalidEnumValue'),
  FIELD_UPLOAD_FAILED: plainFieldErrorPresenter('presentation.errors.field.uploadFailed'),
  FIELD_INVALID_CREDENTIALS: plainFieldErrorPresenter(
    'presentation.errors.field.invalidCredentials'
  ),
  FIELD_MUST_ACCEPT: plainFieldErrorPresenter('presentation.errors.field.mustAccept'),
  FIELD_NOT_ALLOWED: plainFieldErrorPresenter('presentation.errors.field.notAllowed'),
  FIELD_TOO_FEW: tooFew,
  FIELD_TOO_MANY: tooMany,
  FIELD_TOO_SMALL: tooSmall,
  FIELD_TOO_LARGE: plainFieldErrorPresenter('presentation.errors.field.atMost'),
  FIELD_FILE_TOO_LARGE: fileTooLarge,
  FIELD_INVALID_FILE_TYPE: plainFieldErrorPresenter('presentation.errors.field.invalidFileType'),
  FIELD_WRONG_LENGTH: plainFieldErrorPresenter('presentation.errors.field.wrongLength'),
  FIELD_DATE_TOO_EARLY: dateBound(true),
  FIELD_DATE_TOO_LATE: dateBound(false),
  FIELD_DUPLICATE_VALUE: plainFieldErrorPresenter('presentation.errors.field.duplicateValue'),
  FIELD_INCORRECT_CURRENT_PASSWORD: plainFieldErrorPresenter(
    'presentation.errors.field.incorrectCurrentPassword'
  ),
  FIELD_REQUIRED_WITH: plainFieldErrorPresenter('presentation.errors.field.requiredWith'),
  FIELD_REQUIRED_WITHOUT: plainFieldErrorPresenter('presentation.errors.field.requiredWithout'),
  FIELD_UNAVAILABLE: plainFieldErrorPresenter('presentation.errors.field.unavailable'),
  FIELD_ORDER_NOT_MODIFIABLE: plainFieldErrorPresenter(
    'presentation.errors.field.orderNotModifiable'
  ),
  FIELD_MODIFICATION_PENDING: plainFieldErrorPresenter(
    'presentation.errors.field.modificationPending'
  ),
  FIELD_NO_CHANGES: plainFieldErrorPresenter('presentation.errors.field.noChanges'),
  FIELD_INVALID_STATUS_TRANSITION: plainFieldErrorPresenter(
    'presentation.errors.field.invalidStatusTransition'
  ),
  FIELD_BLOCKED_BY_OPEN_ORDERS: plainFieldErrorPresenter(
    'presentation.errors.field.blockedByOpenOrders'
  ),
  FIELD_OVERLAPS: plainFieldErrorPresenter('presentation.errors.field.overlaps'),
  FIELD_INVALID_TOKEN: plainFieldErrorPresenter('presentation.errors.field.invalidToken'),
  FIELD_INVALID_CODE: plainFieldErrorPresenter('presentation.errors.field.invalidCode'),
  FIELD_CAPTCHA_EXPIRED: plainFieldErrorPresenter('presentation.errors.field.captchaExpired'),
  FIELD_CAPTCHA_INVALID: plainFieldErrorPresenter('presentation.errors.field.captchaInvalid'),
  FIELD_CAPTCHA_LOW_SCORE: () => ({
    key: 'presentation.errors.field.captchaLowScore',
    values: { supportEmail: useRuntimeConfig().public.supportEmail }
  }),
  FIELD_PASSWORD_MISSING_LETTER: plainFieldErrorPresenter(
    'presentation.errors.field.passwordMissingLetter'
  ),
  FIELD_PASSWORD_MISSING_MIXED_CASE: plainFieldErrorPresenter(
    'presentation.errors.field.passwordMissingMixedCase'
  ),
  FIELD_PASSWORD_MISSING_NUMBER: plainFieldErrorPresenter(
    'presentation.errors.field.passwordMissingNumber'
  ),
  FIELD_PASSWORD_MISSING_SYMBOL: plainFieldErrorPresenter(
    'presentation.errors.field.passwordMissingSymbol'
  ),
  FIELD_PASSWORD_COMPROMISED: plainFieldErrorPresenter(
    'presentation.errors.field.passwordCompromised'
  )
} satisfies Record<GeneratedFieldErrorCode, FieldErrorPresenter>)
